import { useCallback, useMemo, useState } from 'react';

import _ from 'lodash';

import {
  CONDITION_DECISION,
  CONDITION_ROW_STATE,
} from '../../ConditionReviewList/ConditionReviewList.constants';

import { EMPTY_EDITS, EMPTY_SENT_BACK, omitKey } from './utils';

const useConditionReview = (conditions = []) => {
  const [editsByIndex, setEditsByIndex] = useState(EMPTY_EDITS);
  const [sentBackByIndex, setSentBackByIndex] = useState(EMPTY_SENT_BACK);
  const [removedIndexes, setRemovedIndexes] = useState(() => new Set());
  const [editingIndex, setEditingIndex] = useState(null);

  const beginEdit = useCallback(index => {
    setEditingIndex(index);
    setSentBackByIndex(current => omitKey(current, index));
    setRemovedIndexes(current => {
      if (!current.has(index)) {
        return current;
      }

      const next = new Set(current);
      next.delete(index);

      return next;
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const saveEdit = useCallback((index, edit) => {
    setEditsByIndex(current => ({
      ...current,
      [index]: {
        editedInternalText: edit.editedInternalText ?? null,
        editedBorrowerText: edit.editedBorrowerText ?? null,
        editedPriorToTypeId: edit.editedPriorToTypeId ?? null,
        editedPriorToType: edit.editedPriorToType ?? null,
        editedTasks: edit.editedTasks ?? null,
      },
    }));
    setEditingIndex(null);
  }, []);

  const beginSendBack = useCallback(index => {
    setSentBackByIndex(current => ({ ...current, [index]: current[index] ?? '' }));
    setEditsByIndex(current => omitKey(current, index));
    setEditingIndex(current => (current === index ? null : current));
    setRemovedIndexes(current => {
      if (!current.has(index)) {
        return current;
      }

      const next = new Set(current);
      next.delete(index);

      return next;
    });
  }, []);

  const setSendBackFeedback = useCallback((index, feedback) => {
    setSentBackByIndex(current => ({ ...current, [index]: feedback }));
  }, []);

  const cancelSendBack = useCallback(index => {
    setSentBackByIndex(current => omitKey(current, index));
  }, []);

  const remove = useCallback(index => {
    setRemovedIndexes(current => {
      const next = new Set(current);
      next.add(index);

      return next;
    });
    setEditsByIndex(current => omitKey(current, index));
    setSentBackByIndex(current => omitKey(current, index));
    setEditingIndex(current => (current === index ? null : current));
  }, []);

  const addBack = useCallback(index => {
    setRemovedIndexes(current => {
      if (!current.has(index)) {
        return current;
      }

      const next = new Set(current);
      next.delete(index);

      return next;
    });
  }, []);

  const getRowState = useCallback(
    index => {
      if (editingIndex === index) {
        return CONDITION_ROW_STATE.EDIT;
      }

      if (removedIndexes.has(index)) {
        return CONDITION_ROW_STATE.REMOVE;
      }

      if (!_.isNil(sentBackByIndex[index])) {
        return CONDITION_ROW_STATE.REJECT;
      }

      return CONDITION_ROW_STATE.KEEP;
    },
    [editingIndex, removedIndexes, sentBackByIndex],
  );

  const getEdit = useCallback(index => editsByIndex[index] ?? null, [editsByIndex]);
  const getSendBackFeedback = useCallback(index => sentBackByIndex[index] ?? '', [sentBackByIndex]);

  const hasAnyAction = useMemo(
    () => !_.isEmpty(editsByIndex) || !_.isEmpty(sentBackByIndex) || removedIndexes.size > 0,
    [editsByIndex, sentBackByIndex, removedIndexes],
  );

  const summary = useMemo(() => {
    const removedCount = removedIndexes.size;
    const sentBackCount = Object.keys(sentBackByIndex).length;
    const addCount = Math.max(conditions.length - removedCount - sentBackCount, 0);

    return { addCount, sentBackCount, removedCount };
  }, [conditions.length, removedIndexes, sentBackByIndex]);

  const buildConditionDecisions = useCallback(() => {
    if (!hasAnyAction) {
      return null;
    }

    return conditions.map(condition => {
      const { index } = condition;

      if (removedIndexes.has(index)) {
        return { index, decision: CONDITION_DECISION.REMOVE };
      }

      if (!_.isNil(sentBackByIndex[index])) {
        return {
          index,
          decision: CONDITION_DECISION.REJECT,
          feedback: sentBackByIndex[index] || null,
        };
      }

      const edit = editsByIndex[index];

      if (edit) {
        return { index, decision: CONDITION_DECISION.EDIT, ...edit };
      }

      return { index, decision: CONDITION_DECISION.KEEP };
    });
  }, [conditions, editsByIndex, hasAnyAction, removedIndexes, sentBackByIndex]);

  return {
    beginEdit,
    cancelEdit,
    saveEdit,
    beginSendBack,
    setSendBackFeedback,
    cancelSendBack,
    remove,
    addBack,
    getRowState,
    getEdit,
    getSendBackFeedback,
    hasAnyAction,
    summary,
    buildConditionDecisions,
  };
};

export default useConditionReview;
